import React, { useState, useEffect } from 'react';
import TopBar from '../../components/layout/TopBar';
import ProfileSettings from './ProfileSettings';
import BillingSettings from './BillingSettings';
import UsageSettings from './UsageSettings';
import PeopleSettings from './PeopleSettings';
import userService from '../../services/userServices';
import styles from './SettingsPage.module.css';
import { User, CreditCard, BarChart3, Users } from 'lucide-react';

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState('billing');
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  useEffect(() => {
    // Load user profile data from API
    const loadUserProfile = async () => {
      try {
        setLoading(true);
        setProfileError(null);
        console.log('[Settings] Loading user profile...');
        const response = await userService.getProfile();
        console.log('[Settings] Profile response:', response);
        if (response && response.data) {
          setUserProfile(response.data);
          console.log('[Settings] Profile loaded successfully:', response.data);
        } else {
          console.error('[Settings] Failed to load user profile:', response);
          setProfileError('Failed to load profile data from server');
        }
      } catch (error) {
        console.error('[Settings] Failed to load user profile:', error);
        setProfileError('Failed to load profile information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings userProfile={userProfile} loading={loading} error={profileError} />;
      case 'billing':
        return <BillingSettings />;
      case 'usage':
        return <UsageSettings />;
      case 'people':
        return <PeopleSettings />;
      default:
        return <BillingSettings />;
    }
  };

  return (
    <div className={styles.settingsPage}>
      <TopBar isLanding={true} />
      
      {/* Background layers matching landing page exactly */}
      <div className="fixed inset-0 -z-10">
        {/* Dark mode background */}
        <div className="dark:block hidden absolute inset-0 bg-black" />
        
        {/* Light mode background */}
        <div className="light:block hidden absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
        
        {/* Dark mode enhanced effects - matching landing page exactly */}
        <div className="dark:block hidden">
          {/* Futuristic background patterns - matching landing page */}
          <div className="absolute inset-0" style={{
            background: `
              radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(0, 255, 255, 0.02) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(0, 255, 255, 0.02) 0%, transparent 50%)
            `
          }} />
        </div>

        {/* Light mode enhanced effects */}
        <div className="light:block hidden">
          {/* Light mode background patterns */}
          <div className="absolute inset-0" style={{
            background: `
              radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)
            `
          }} />
        </div>

        {/* Dot grid background - matching landing page exactly */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 dark:block light:hidden" style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, 
                rgba(0, 255, 255, 0.15) 1px, 
                transparent 0
              )
            `,
            backgroundSize: '20px 20px'
          }} />
          <div className="absolute inset-0 dark:hidden light:block" style={{
            backgroundImage: `
              radial-gradient(circle at 1px 1px, 
                rgba(59, 130, 246, 0.2) 1px, 
                transparent 0
              )
            `,
            backgroundSize: '24px 24px'
          }} />
        </div>
      </div>
      
      <div className={styles.settingsContainer}>
        {/* Left Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h2>Settings</h2>
          </div>
          
          <nav className={styles.sidebarNav}>
            <button
              className={`${styles.navItem} ${activeSection === 'billing' ? styles.active : ''}`}
              onClick={() => setActiveSection('billing')}
            >
              <CreditCard className={styles.navIcon} /> Billing
            </button>
            <button
              className={`${styles.navItem} ${activeSection === 'usage' ? styles.active : ''}`}
              onClick={() => setActiveSection('usage')}
            >
              <BarChart3 className={styles.navIcon} /> Usage
            </button>
            <button
              className={`${styles.navItem} ${activeSection === 'profile' ? styles.active : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              <User className={styles.navIcon} /> Profile
            </button>
            <button
              className={`${styles.navItem} ${activeSection === 'people' ? styles.active : ''}`}
              onClick={() => setActiveSection('people')}
            >
              <Users className={styles.navIcon} /> People
            </button>
            {/* Add more settings sections here as needed */}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
}
